import React, { useContext, useEffect, useState } from "react";
import { Badge, Button, Col, Form, Form as BTForm, Row } from "react-bootstrap";
import {
  dateFormatter,
  dateTimeFormatter,
  dateTimeFormatterWithDay,
} from "../../../../../global";
import history from "../../../../../global/history";
import Card from "src/components/Cards/Basic";
import axios from "src/config/axios";
import CashIcon from "src/images/icons/cash.png";
import CreditCardIcon from "src/images/icons/credit-card.png";
import {
  faAdd,
  faClockRotateLeft as HistoryIcon,
  faEye as DetailsIcon,
  faInfoCircle,
  faPencil as EditIcon,
  faSearch,
  faTriangleExclamation as WarnIcon,
} from "@fortawesome/free-solid-svg-icons";
import SimplePagination from "../../../../../components/SimplePagination";
import { useTranslation } from "react-i18next";
import Table from "../../../../../components/Tables/Table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TableIconButton from "../../../../../components/UiComponents/Buttons/TableIconButton";
import OrderButtonDropdown from "../../../../../components/UiComponents/Buttons/OrderButtonDropdown";
import FabPlateau from "../../../../../components/UiComponents/Buttons/FabPlateau";
import OnlineReservationDetailsDialog from "../../../OnlineReservations/OnlineReservationDetailsDialog";
import { modifyState } from "../../../../../components/UiComponents/Buttons/BackButton/helper";
import FilterResetButton from "../../../../../components/UiComponents/Buttons/FilterResetButton";
import LegendItem from "../../../../../components/Legend/LegendItem";
import Legend from "../../../../../components/Legend";
import { Link } from "react-router-dom";
import useFilter from "../../../../../global/useFilter";
import { useLocation } from "react-router-dom/cjs/react-router-dom";
import BookingHistoryDialog from "../BookingHistoryDialog";
import { DashboardContext } from "../../../../../routes/DashboardRoutes";
import { useDebounce } from "use-debounce";
import { linkBuilder, navigateTo } from "src/global/navigationHelper";

const BookingTable = ({ title, getBookings }) => {
  const [bookings, setBookings] = useState();
  const [bookingHistory, setBookingHistory] = useState();
  const { settings } = useContext(DashboardContext);
  const { t } = useTranslation("dashboard");
  const [parkingSlots, setParkingSlots] = useState([]);
  const [filterShow, setFilterShow] = useState(false);
  const businessSettings = settings?.business;
  const [onlineReservation, setOnlineReservation] = useState(null);
  const [selectedBookingForHistoryDialog, setSelectedBookingForHistoryDialog] =
    useState(null);
  const showOnlineReservationDialog = onlineReservation !== null;
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { pathname } = location;

  let columns = [
    { value: "buyer", name: "Käufer" },
    { value: "booked_from", name: "Anreise" },
    { value: "booked_until", name: "Abreise" },
    { value: "booking_type", name: "Typ" },
    { value: "paid", name: "Bezahlt" },
    { value: "created_at", name: "Erstellt am" },
    { value: "", name: "Optionen" },
  ];

  const loadingColumns = [
    { value: "buyer", name: "Käufer" },
    { value: "booked_from", name: "Anreise" },
    { value: "booked_until", name: "Abreise" },
    { value: "booking_type", name: "Typ" },
    { value: "options", name: "Optionen" },
  ];

  const parkingSlotColumnPos = 1;
  if (businessSettings?.parking_slots_enabled)
    columns.splice(parkingSlotColumnPos, 0, {
      value: "parking_slot",
      name: "Stellplatz",
    });

  const defaultFilter = {
    search: "",
    date: new Date(),
  };

  const { filter, setFilter, resetFilter } = useFilter(pathname, defaultFilter);
  const [searchValue] = useDebounce(filter.search, 400);

  async function applyFilter() {
    setLoading(true);
    const result = await getBookings(filter);
    setBookings(result.data);
    setLoading(false);
  }

  const searchBookings = (search) => {
    let sanitizedSearch = search;
    sanitizedSearch = sanitizedSearch.replaceAll(":", "");

    setFilter({ ...filter, search: sanitizedSearch, page: 1 });
  };

  const goToPage = (page) => {
    setFilter({ ...filter, page: page });
  };

  function createBooking() {
    navigateTo("/dashboard/bookings/new");
  }

  function handleReservationUpdated() {
    applyFilter();
    setOnlineReservation(null);
  }

  useEffect(() => {
    applyFilter();
  }, [
    filter.date,
    filter.parking_slot_id,
    filter.order,
    filter.parking_slot_id,
    filter.page,
    filter.hide_past_bookings,
    searchValue,
  ]);

  useEffect(() => {
    if (businessSettings?.parking_slots_enabled) {
      axios
        .get("/api/v1/parking_slots.json")
        .then(function (response) {
          setParkingSlots(response.data.entries);
        })
        .catch(function (error) {
          console.error(error);
        });
    }
  }, [businessSettings]);

  function renderFilters() {
    if (!filterShow) {
      return <></>;
    }
    return (
      <>
        <Row className="optional-filter-container align-items-center"></Row>
      </>
    );
  }

  const header = (
    <div className="d-flex flex-wrap w-100 justify-content-between">
      <span>
        {title} ({bookings?.pagination?.count ?? 0})
      </span>
      <FilterResetButton
        resetCallback={() => {
          resetFilter();
        }}
      />
    </div>
  );

  return (
    <>
      <Card title={header}>
        <div className="mb-3">
          <Row className="filter-container">
            <Col
              md={6}
              xs={12}
              className="d-flex mb-4 mb-sm-0 justify-content-between justify-content-sm-start"
            >
              <OrderButtonDropdown
                selected={filter.order}
                orders={[
                  { key: "created_at_desc", value: "Neueste zuerst" },
                  { key: "created_at_asc", value: "Älteste zuerst" },
                  { key: "booked_from_asc", value: "Anreise aufsteigend" },
                  { key: "booked_from_desc", value: "Anreise absteigend" },
                  { key: "booked_until_asc", value: "Abreise aufsteigend" },
                  { key: "booked_until_desc", value: "Abreise absteigend" },
                ]}
                callback={(selected_value) => {
                  setFilter({
                    ...filter,
                    order: selected_value,
                  });
                }}
              />
            </Col>
            <Col md={3} xs={12} className={"d-sm-block"}>
              {businessSettings?.parking_slots_enabled &&
                parkingSlots.length > 0 && (
                  <Form.Group className="mb-2">
                    <Form.Label>Stellplatz</Form.Label>
                    <Form.Control
                      as={"select"}
                      className="form-control ui-input-bg"
                      value={filter.parking_slot_id ?? ""}
                      onChange={(e) => {
                        setFilter({
                          ...filter,
                          parking_slot_id:
                            e.target.value === "null"
                              ? undefined
                              : e.target.value,
                          page: 1,
                        });
                      }}
                    >
                      <option value="null">Alle</option>
                      {parkingSlots.map((parkingSlot) => (
                        <option key={parkingSlot.id} value={parkingSlot.id}>
                          {parkingSlot.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                )}
              {pathname === "/dashboard/bookings" && (
                <BTForm.Check
                  label="Vergangene Buchungen ausblenden"
                  name="hide_past_bookings"
                  type="checkbox"
                  value={!!filter.hide_past_bookings}
                  onChange={() =>
                    setFilter({
                      ...filter,
                      hide_past_bookings: !filter.hide_past_bookings,
                    })
                  }
                  checked={!!filter.hide_past_bookings}
                  className={"mr-3 mb-3"}
                  id={"hide_past_bookings"}
                />
              )}
            </Col>
            <Col md={3} xs={12}>
              <Form.Group className="mb-2">
                <Form.Label>Suche</Form.Label>
                <div className="position-relative d-flex">
                  <div className="position-absolute p-2">
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                  <Form.Control
                    placeholder="Vorname, Nachname, Karte ..."
                    className="input-icon-p ui-input-bg"
                    type="text"
                    defaultValue={filter.search}
                    onChange={(e) => searchBookings(e.target.value)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          {renderFilters()}
          <div className="ui-table-container">
            <Table
              columns={loading ? loadingColumns : columns}
              empty={!loading && bookings?.entries?.length === 0}
              emptyMessage="Keine Buchungen vorhanden"
            >
              {loading ? (
                <>
                  {Array.from({ length: 10 }, (_, i) => (
                    <tr key={i}>
                      <td>
                        <div className={"preview w-100px h-25px"}></div>
                      </td>
                      <td>
                        <div className={"preview w-100px h-25px"}></div>
                      </td>
                      <td>
                        <div className={"preview w-100px h-25px"}></div>
                      </td>
                      <td>
                        <div className={"preview w-100px h-25px"}></div>
                      </td>
                      <td>
                        <div className={"preview w-100px h-25px"}></div>
                      </td>
                    </tr>
                  ))}
                </>
              ) : (
                <>
                  {bookings &&
                    bookings.entries.map((booking) => (
                      <tr key={booking.id}>
                        <td>
                          <span>
                            {booking.stay?.contact_full_name
                              ? booking.stay?.contact_full_name
                              : "Gast unbekannt"}
                          </span>
                          {businessSettings?.guest_registration_enabled &&
                            booking.state === "active" &&
                            !booking.stay.initial_checkin_at && (
                              <span>
                                &nbsp;
                                <FontAwesomeIcon
                                  icon={WarnIcon}
                                  className={"text-warning"}
                                />
                              </span>
                            )}
                          <br />
                          <Badge
                            pill
                            variant={
                              booking.state === "draft"
                                ? "secondary"
                                : "primary"
                            }
                            text="light"
                          >
                            {t(`booking.statuses.${booking.state}`)}
                          </Badge>
                        </td>
                        {businessSettings?.parking_slots_enabled && (
                          <td>{booking.parking_slot?.name}</td>
                        )}
                        <td>
                          {booking.booked_from && (
                            <>
                              <div>
                                {dateTimeFormatterWithDay(booking.booked_from)}
                              </div>
                            </>
                          )}
                        </td>
                        <td>
                          {booking.stay.to && (
                            <>
                              <div>
                                {dateTimeFormatterWithDay(booking.stay.to)}
                              </div>
                            </>
                          )}
                          {booking.stay.checkout_confirmed_at && (
                            <>
                              <strong>
                                Checkout:{" "}
                                {dateTimeFormatter(
                                  booking.stay.checkout_confirmed_at
                                )}
                              </strong>
                            </>
                          )}
                        </td>
                        <td>
                          <div>{booking.booking_type.name}</div>
                        </td>
                        <td>
                          {booking.paid &&
                            (booking.stay?.last_invoice?.payment_type ===
                            "cash" ? (
                              <img
                                className="w-25px"
                                src={CashIcon}
                                alt="cash"
                              />
                            ) : ["viva", "stripe", "card"].includes(
                                booking.stay?.last_invoice?.payment_type
                              ) ? (
                              <img
                                className="w-25px"
                                src={CreditCardIcon}
                                alt="card"
                              />
                            ) : null)}
                        </td>
                        <td>
                          {booking.created_at && (
                            <>
                              <div>{dateFormatter(booking.created_at)}</div>
                            </>
                          )}
                        </td>
                        <td>
                          <div>
                            <>
                              <Link
                                to={{
                                  pathname: `/dashboard/stays/${booking.stay_id}`,
                                  state: modifyState(
                                    history,
                                    location.pathname
                                  ),
                                }}
                              >
                                <TableIconButton
                                  icon={DetailsIcon}
                                  variant={"primary"}
                                />
                              </Link>

                              {[
                                "reservation_requested",
                                "reservation_accepted",
                              ].includes(booking.state) && (
                                <Link
                                  to={{
                                    pathname: linkBuilder(
                                      `/dashboard/stays/${booking.stay_id}/bookings/${booking.id}/edit`
                                    ),
                                    state: modifyState(history, location.pathname),
                                  }}
                                >
                                  <TableIconButton icon={EditIcon} />
                                </Link>
                              )}

                              <TableIconButton
                                icon={HistoryIcon}
                                onClick={() =>
                                  setSelectedBookingForHistoryDialog(booking)
                                }
                              />
                            </>
                          </div>
                        </td>
                      </tr>
                    ))}
                </>
              )}
            </Table>
          </div>
        </div>
        <SimplePagination
          pagination={bookings?.pagination}
          onClick={(page) => goToPage(page)}
        />
        <FabPlateau>
          <Button
            className="float-right p-2 pl-3 pr-3"
            variant="primary"
            onClick={createBooking}
          >
            <FontAwesomeIcon icon={faAdd} /> &nbsp;Neue Buchung
          </Button>
        </FabPlateau>

        {businessSettings?.guest_registration_enabled && (
          <Legend>
            <LegendItem
              icon={faInfoCircle}
              text={<b>Erklärung:</b>}
              classNames="font-weight-bold"
            />
            <LegendItem
              icon={WarnIcon}
              text="Check-In ausständig"
              classNames="text-warning"
            />
          </Legend>
        )}
      </Card>

      {selectedBookingForHistoryDialog && (
        <BookingHistoryDialog
          booking={selectedBookingForHistoryDialog}
          handleClose={() => setSelectedBookingForHistoryDialog(null)}
        />
      )}

      {showOnlineReservationDialog && (
        <OnlineReservationDetailsDialog
          reservationBookingId={onlineReservation.id}
          handleClose={() => setOnlineReservation(null)}
          handleSubmit={handleReservationUpdated}
        />
      )}
    </>
  );
};

export default BookingTable;
